import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  assistentes as api,
  marcas as apiMarcas,
  type Assistente,
  type CaixaNaLista,
  type AssistenteNaLista,
  type Brevidade,
  type Formalidade,
  type PapelDoAssistente,
  type Diferenca,
  type PreviaDoPrompt,
  type VersaoDePromptNaLista,
} from '../api/assistentes'
import { AssistenteNovo } from './AssistenteNovo'
import { EditorDeRegras } from './EditorDeRegras'
import { PendenciasDePublicacao } from '../componentes/PendenciasDePublicacao'
import { IdentidadeNaPlataforma } from './IdentidadeNaPlataforma'
import { ModeloDoAssistente } from './ModeloDoAssistente'
import { ErroApi } from '../api/client'
import { times as apiTimes, type TimeNaLista } from '../api/times'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Campo } from '../componentes/Campo'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { useSessao } from '../auth/useSessao'
import { formatar } from '../i18n'
import './usuarios.css'
import './assistentes.css'
import { marcaDaRota } from './marcaDaRota'

const BREVIDADES: Brevidade[] = ['Curta', 'Media', 'Detalhada']
const FORMALIDADES: Formalidade[] = ['Informal', 'Cordial', 'Formal']

/** A marca vem do caminho: /marcas/:marca/assistentes */

export function Assistentes() {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const marca = (marcaDaRota() ?? '')

  const [lista, setLista] = useState<AssistenteNaLista[] | null>(null)
  const [falhou, setFalhou] = useState(false)
  const [editando, setEditando] = useState<number | null>(null)
  const [criando, setCriando] = useState(false)

  const carregar = useCallback(async () => {
    setFalhou(false)
    try {
      setLista(await api.listar(marca))
    } catch {
      setFalhou(true)
    }
  }, [marca])

  useEffect(() => {
    void carregar()
  }, [carregar])

  if (falhou)
    return <Estado tipo="erro" acao={<Botao onClick={carregar}>{t('comum.tentarDeNovo')}</Botao>} />

  if (!lista) return <Estado tipo="carregando" />

  if (criando)
    return (
      <AssistenteNovo
        marca={marca}
        aoFechar={() => setCriando(false)}
        aoCriar={async (id) => {
          setCriando(false)
          await carregar()
          setEditando(id)
        }}
      />
    )

  if (editando !== null)
    return (
      <Formulario
        marca={marca}
        id={editando}
        aoFechar={() => setEditando(null)}
        aoSalvar={async () => {
          setEditando(null)
          await carregar()
        }}
      />
    )

  return (
    <main className="usuarios">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">{t('assistentes.titulo')}</h1>
          <p className="usuarios__descricao">{t('assistentes.descricao', { marca })}</p>
        </div>

        {pode('ParametroGerenciar') && (
          <Botao variante="primario" onClick={() => setCriando(true)}>
            {t('assistentes.novo')}
          </Botao>
        )}
      </header>

      {lista.length === 0 ? (
        <Estado
          tipo="vazio"
          descricao={t('assistentes.vazio')}
          acao={
            pode('ParametroGerenciar') ? (
              <Botao variante="primario" onClick={() => setCriando(true)}>
                {t('assistentes.novo')}
              </Botao>
            ) : undefined
          }
        />
      ) : (
        <div
            className="usuarios__tabela-rolagem"
            // A região rolável precisa ser alcançável pelo teclado (WCAG 2.1.1):
            // sem isto, a parte escondida da tabela não existe para quem não usa mouse.
            tabIndex={0}
            role="group"
            aria-label={t('assistentes.legenda')}
          >
            <table className="usuarios__tabela">
            <caption className="usuarios__legenda">{t('assistentes.legenda')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('assistentes.nome')}</th>
                <th scope="col">{t('assistentes.canal')}</th>
                <th scope="col">{t('assistentes.modelo')}</th>
                <th scope="col">{t('usuarios.situacao')}</th>
                <th scope="col">{t('usuarios.acoes')}</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((a) => (
                <tr key={a.id}>
                  <td>{a.nome}</td>
                  <td>{a.caixas.length > 0 ? a.caixas.join(' · ') : t('assistentes.semCanal')}</td>
                  <td>
                    {a.provedor} · {a.modelo}
                  </td>
                  <td>
                    <span className={`etiqueta etiqueta--${a.ativo ? 'ativo' : 'inativo'}`}>
                      {a.ativo ? t('usuarios.ativo') : t('usuarios.inativo')}
                    </span>
                    {a.papel !== 'Atendimento' && (
                      <span className="etiqueta etiqueta--pendente">
                        {t(`assistentes.papel${a.papel}`)}
                      </span>
                    )}
                  </td>
                  <td>
                    <Botao
                      variante="discreto"
                      onClick={() => setEditando(a.id)}
                      disabled={!pode('ParametroVer')}
                    >
                      {pode('ParametroGerenciar') ? t('usuarios.editar') : t('assistentes.ver')}
                    </Botao>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  )
}

function Formulario({
  marca,
  id,
  aoFechar,
  aoSalvar,
}: {
  marca: string
  id: number
  aoFechar: () => void
  aoSalvar: () => Promise<void>
}) {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const [dados, setDados] = useState<Assistente | null>(null)
  const [previa, setPrevia] = useState<PreviaDoPrompt | null>(null)
  const [falhou, setFalhou] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  // Recarrega o aviso da 123 a cada gravação — ver `Regras.tsx`.
  const [gravacoes, setGravacoes] = useState(0)

  const editavel = pode('ParametroGerenciar')
  const [timesDisponiveis, setTimes] = useState<TimeNaLista[]>([])
  const [canais, setCanais] = useState<CaixaNaLista[]>([])

  const recarregarPrevia = useCallback(async () => {
    try {
      setPrevia(await api.prompt(marca, id))
    } catch {
      setPrevia(null)
    }
  }, [marca, id])

  useEffect(() => {
    api
      .ler(marca, id)
      .then(setDados)
      .catch(() => setFalhou(true))

    // Time ausente não entra na lista de escolha: oferecer um destino que
    // sumiu do Chatwoot é mandar a conversa para o vazio.
    apiTimes
      .listar()
      .then((t) => setTimes(t.filter((x) => !x.ausente)))
      .catch(() => setTimes([]))

    apiMarcas.caixas(marca).then(setCanais).catch(() => setCanais([]))

    void recarregarPrevia()
  }, [marca, id, recarregarPrevia])

  if (falhou) return <Estado tipo="erro" />
  if (!dados) return <Estado tipo="carregando" />

  const alterar = <C extends keyof Assistente>(campo: C, valor: Assistente[C]) =>
    setDados({ ...dados, [campo]: valor })

  async function salvar(evento: FormEvent) {
    evento.preventDefault()
    if (!dados) return

    setErro(null)
    setEnviando(true)

    try {
      await api.salvar(marca, id, dados)

      // A pré-visualização é recarregada depois de salvar: é ela que mostra o
      // efeito da mudança no texto que o modelo recebe.
      await recarregarPrevia()
      await aoSalvar()
      setGravacoes((n) => n + 1)
    } catch (e) {
      const corpo = e instanceof ErroApi ? (e.corpo as { detail?: string } | null) : null
      setErro(corpo?.detail ?? t('comum.erroDescricao'))
    } finally {
      setEnviando(false)
    }
  }

  return (
    <main className="usuarios">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">{dados.nome}</h1>
          <p className="usuarios__descricao">{t('assistentes.formularioDescricao')}</p>
        </div>

        <Botao variante="discreto" onClick={aoFechar}>
          {t('assistentes.voltar')}
        </Botao>
      </header>

      {erro && <Aviso tom="perigo">{erro}</Aviso>}

      {!editavel && <Aviso tom="info">{t('assistentes.somenteLeitura')}</Aviso>}

      {/* 🔴 **Persona e instruções entram no retrato publicado** — foi aqui que
          se descobriu, em 31/08, que as instruções da Letrinhas em produção
          eram de 17/08, cerca de 2.000 caracteres atrás do que estava gravado.
          Sem este aviso, editar o comportamento e não publicar é invisível. */}
      <PendenciasDePublicacao recarregar={gravacoes} />

      <div className="assistente__colunas">
        <Cartao titulo={t('assistentes.comportamento')}>
          <form onSubmit={salvar} noValidate>
            <Campo
              rotulo={t('assistentes.nome')}
              value={dados.nome}
              onChange={(e) => alterar('nome', e.target.value)}
              disabled={!editavel}
              ajuda={t('assistentes.nomeAjuda')}
              obrigatorio
            />

            <label className="usuarios__filtro">
              <span>{t('assistentes.papel')}</span>
              <select
                value={dados.papel}
                disabled={!editavel}
                onChange={(e) => alterar('papel', e.target.value as PapelDoAssistente)}
              >
                <option value="Atendimento">{t('assistentes.papelAtendimento')}</option>
                <option value="Interno">{t('assistentes.papelInterno')}</option>
                <option value="Copiloto">{t('assistentes.papelCopiloto')}</option>
              </select>
            </label>

            {dados.papel === 'Copiloto' && (
              <Aviso tom="alerta">{t('assistentes.papelCopilotoAviso')}</Aviso>
            )}

            <Campo
              rotulo={t('assistentes.persona')}
              value={dados.persona}
              onChange={(e) => alterar('persona', e.target.value)}
              disabled={!editavel}
              ajuda={t('assistentes.personaAjuda')}
            />

            {/* 🔴 Sem isto, a Giulia que JÁ existe não ganhava o Instagram: a
                marcação múltipla ficou só no formulário de criação, e o caso
                real é sempre o assistente que já está no ar. */}
            <fieldset className="usuarios__grupo">
              <legend>{t('assistentes.canal')}</legend>
              <p className="usuarios__descricao">{t('assistentes.canalAjuda')}</p>

              {canais.length === 0 ? (
                <Estado tipo="vazio" titulo={t('assistentes.semCanalCadastrado')} />
              ) : (
                <ul className="assistentes__canais">
                  {canais.map((c) => {
                    // Ocupado por OUTRO. O que este assistente já atende não é
                    // ocupação: é o estado dele.
                    const deOutro = c.assistente !== null && !dados.caixasDeEntradaIds.includes(c.id)
                    const ligado = dados.caixasDeEntradaIds.includes(c.id)

                    return (
                      <li key={c.id}>
                        <label className={deOutro ? 'assistentes__canal--ocupado' : undefined}>
                          <input
                            type="checkbox"
                            checked={ligado}
                            disabled={!editavel || deOutro}
                            onChange={(e) =>
                              alterar(
                                'caixasDeEntradaIds',
                                e.target.checked
                                  ? [...dados.caixasDeEntradaIds, c.id]
                                  : dados.caixasDeEntradaIds.filter((id) => id !== c.id),
                              )
                            }
                          />
                          {c.nome} · {t('assistentes.inbox', { id: c.idExterno })}
                          {deOutro && ` — ${t('assistentes.jaOcupado', { nome: c.assistente })}`}
                        </label>
                      </li>
                    )
                  })}
                </ul>
              )}
            </fieldset>

            <fieldset className="usuarios__grupo">
              <legend>{t('assistentes.brevidade')}</legend>
              {BREVIDADES.map((b) => (
                <label key={b} className="usuarios__opcao">
                  <input
                    type="radio"
                    name="brevidade"
                    checked={dados.brevidade === b}
                    disabled={!editavel}
                    onChange={() => alterar('brevidade', b)}
                  />
                  <span>
                    <strong>{t(`assistentes.brevidade${b}`)}</strong>
                    <small>{t(`assistentes.brevidade${b}Ajuda`)}</small>
                  </span>
                </label>
              ))}
            </fieldset>

            <fieldset className="usuarios__grupo">
              <legend>{t('assistentes.formalidade')}</legend>
              {FORMALIDADES.map((f) => (
                <label key={f} className="usuarios__opcao">
                  <input
                    type="radio"
                    name="formalidade"
                    checked={dados.formalidade === f}
                    disabled={!editavel}
                    onChange={() => alterar('formalidade', f)}
                  />
                  <span>
                    <strong>{t(`assistentes.formalidade${f}`)}</strong>
                  </span>
                </label>
              ))}
            </fieldset>

            <Campo
              rotulo={t('assistentes.aviso')}
              value={dados.avisoDeAutomacao}
              onChange={(e) => alterar('avisoDeAutomacao', e.target.value)}
              disabled={!editavel}
              ajuda={t('assistentes.avisoAjuda')}
            />

            {/* 🔴 Não entra no prompt do atendimento (041 §3.3). É a "instrução
                mole" do P-38: quem lê o resultado é um atendente humano com a
                conversa aberta ao lado, e errar custa um resumo meia-boca. */}
            <Campo
              rotulo={t('assistentes.destaquesDoResumo')}
              value={dados.destaquesDoResumo ?? ''}
              onChange={(e) => alterar('destaquesDoResumo', e.target.value || null)}
              disabled={!editavel}
              ajuda={t('assistentes.destaquesDoResumoAjuda')}
            />

            {/* 🔴 **Os parâmetros do modelo** (demanda 133). Até 28/08 a
                temperatura era um valor padrão de parâmetro em C# que ninguém
                escrevia: leitor sem escritor, a forma inversa do "campo sem
                leitor" das demandas 052, 059 e 090.

                ⚠️ A ajuda diz o efeito **e o custo**. A queixa da semana foi de
                fluidez, e é tentador oferecer este controle como resposta —
                mas subir a temperatura aumenta a variedade e o risco de
                invenção, que é a outra queixa da mesma reunião. As duas puxam
                para lados opostos e o controle é um só. */}
            <Campo
              rotulo={t('assistentes.temperatura')}
              type="number"
              min={0}
              max={1}
              step={0.05}
              value={dados.temperatura}
              onChange={(e) => alterar('temperatura', Number(e.target.value))}
              disabled={!editavel}
              ajuda={t('assistentes.temperaturaAjuda')}
            />

            <Campo
              rotulo={t('assistentes.maximoDeTokens')}
              type="number"
              min={200}
              max={8000}
              step={100}
              value={dados.maximoDeTokens}
              onChange={(e) => alterar('maximoDeTokens', Number(e.target.value))}
              disabled={!editavel}
              ajuda={t('assistentes.maximoDeTokensAjuda')}
            />

            {/* Escolha na lista, não texto livre — ver AssistenteNovo. */}
            <label className="usuarios__filtro">
              <span>{t('assistentes.setor')}</span>
              <select
                value={dados.setorDeEscalonamento ?? ''}
                onChange={(e) => alterar('setorDeEscalonamento', e.target.value || null)}
                disabled={!editavel}
              >
                <option value="">{t('assistentes.semSetor')}</option>

                {/* O setor gravado continua listado mesmo que o time tenha
                    sumido do Chatwoot: sem isto, abrir a tela apagaria em
                    silêncio um roteamento que alguém configurou. */}
                {timesDisponiveis.some((x) => x.nome === dados.setorDeEscalonamento) ||
                !dados.setorDeEscalonamento ? null : (
                  <option value={dados.setorDeEscalonamento}>
                    {dados.setorDeEscalonamento}
                  </option>
                )}

                {timesDisponiveis.map((time) => (
                  <option key={time.id} value={time.nome}>{time.nome}</option>
                ))}
              </select>
            </label>

            <label className="usuarios__opcao">
              <input
                type="checkbox"
                checked={dados.ativo}
                disabled={!editavel}
                onChange={(e) => alterar('ativo', e.target.checked)}
              />
              <span>
                <strong>{t('assistentes.ativoRotulo')}</strong>
              </span>
            </label>

            {editavel && (
              <div className="usuarios__acoes-formulario">
                <Botao type="submit" variante="primario" carregando={enviando}>
                  {t('comum.salvar')}
                </Botao>
              </div>
            )}
          </form>
        </Cartao>

        <Cartao titulo={t('assistentes.previaTitulo')}>
          <p className="usuarios__descricao">{t('assistentes.previaDescricao')}</p>

          {previa === null ? (
            <Estado tipo="erro" descricao={t('assistentes.previaFalhou')} />
          ) : (
            <>
              <p className="assistente__medida">
                {t('assistentes.caracteres', { total: formatar.numero(previa.caracteres) })}
              </p>

              {/* O texto exato que o modelo recebe. Sem isto ninguém sabe o
                  que está no prompt — e foi assim que a verborragia passou
                  despercebida até a análise de 17/07. */}
              <pre className="assistente__prompt">{previa.prompt}</pre>

              <details className="assistente__regras">
                <summary>{t('assistentes.regrasQueGeraram')}</summary>
                <dl>
                  {previa.regras.map((r) => (
                    <div key={r.chave} className="assistente__regra">
                      <dt>{r.chave}</dt>
                      <dd>{r.valor}</dd>
                    </div>
                  ))}
                </dl>
              </details>
            </>
          )}

          <Historico marca={marca} id={id} versaoAtual={previa?.versao ?? null} />
        </Cartao>
      </div>

      {/* 🔴 As oito decisões do cliente, aqui, com a procedência de cada valor.
          Sem esta seção, mudar a tiragem mínima de livro exigia `INSERT` no
          banco de produção — e em setembro o portal fica com o cliente. */}
      <Cartao titulo={t('regras.noAssistente')}>
        <p className="usuarios__descricao">{t('regras.noAssistenteAjuda')}</p>

        {/* Recarrega a pré-visualização: é nela que a mudança aparece como
            frase nova no texto que o modelo recebe. */}
        <EditorDeRegras marca={marca} assistenteId={id} aoSalvar={recarregarPrevia} />
      </Cartao>

      <Cartao titulo={t('modelo.titulo')}>
        <p className="usuarios__descricao">{t('modelo.descricao')}</p>

        <ModeloDoAssistente
          assistenteId={id}
          provedorAtual={dados.provedor}
          modeloAtual={dados.modelo}
        />
      </Cartao>

      <IdentidadeNaPlataforma marca={marca} id={id} />
    </main>
  )
}

/**
 * O histórico de versões do prompt (`P-07`).
 *
 * O log de auditoria já dizia que alguém mudou o limiar de 1.200 para 2.000. O
 * que faltava era o **efeito**: qual texto o modelo passou a receber.
 *
 * 🔴 O diff é o que torna isso utilizável. Comparar dois blocos de prosa a olho
 * não funciona — foi assim que onze passos numerados entraram no prompt da
 * Giulia sem ninguém notar até a análise de 17/07.
 */
function Historico({
  marca,
  id,
  versaoAtual,
}: {
  marca: string
  id: number
  versaoAtual: number | null
}) {
  const { t } = useTranslation()

  const [versoes, setVersoes] = useState<VersaoDePromptNaLista[] | null>(null)
  const [diferenca, setDiferenca] = useState<Diferenca | null>(null)
  const [comparando, setComparando] = useState<number | null>(null)

  useEffect(() => {
    api
      .versoesDoPrompt(marca, id)
      .then(setVersoes)
      .catch(() => setVersoes([]))
  }, [marca, id, versaoAtual])

  if (!versoes || versoes.length === 0) return null

  async function comparar(numero: number) {
    setComparando(numero)

    try {
      setDiferenca(await api.diferenca(marca, id, numero))
    } catch {
      setDiferenca(null)
    }
  }

  return (
    <details className="assistente__historico">
      <summary>{t('assistentes.historicoDoPrompt', { total: versoes.length })}</summary>

      <ol className="assistente__versoes">
        {versoes.map((v) => (
          <li key={v.numero} className="assistente__versao">
            <span className="assistente__versao-numero">v{v.numero}</span>
            <span className="assistente__versao-detalhe">
              {formatar.dataHora(v.geradoEm)} · {t(`assistentes.origem${v.origem}`)}
              {/* Versão sem autor é a que foi apenas notada — mudança feita
                  direto no banco, ou no código do montador via deploy. É
                  justamente a que ninguém anunciou. */}
              {v.geradoPor ? ` · ${v.geradoPor}` : ` · ${t('assistentes.semAutor')}`}
            </span>
            <Botao variante="discreto" onClick={() => comparar(v.numero)}>
              {t('assistentes.compararComAtual')}
            </Botao>
          </li>
        ))}
      </ol>

      {diferenca && comparando !== null && (
        <div className="assistente__diferenca">
          <p className="assistente__diferenca-resumo">
            {t('assistentes.diferencaResumo', {
              versao: comparando,
              entraram: diferenca.entraram,
              sairam: diferenca.sairam,
            })}
          </p>

          <pre className="assistente__diff">
            {diferenca.linhas.map((linha, i) => (
              <span key={i} className={`assistente__linha assistente__linha--${linha.marca}`}>
                {/* O sinal em texto, não só a cor de fundo (`UI-09`). */}
                {linha.marca === 'entrou' ? '+ ' : linha.marca === 'saiu' ? '- ' : '  '}
                {linha.texto}
                {'\n'}
              </span>
            ))}
          </pre>
        </div>
      )}
    </details>
  )
}
